import React from 'react';
import {
  ArrowRight,
  Clock,
  Shield,
  Zap,
  Globe,
  Users,
  Target,
  CheckCircle,
  TrendingUp
} from 'lucide-react';
import { useLanguage } from '../contexts/LanguageContext';

export const SOSPage = () => {
  const { t } = useLanguage();

  const rawPackages = t('sosPage.packages');
  const packagesMeta = [
    {
      id: 'culturebridge',
      icon: Globe,
      gradient: 'from-purple-600 to-blue-600',
      urgent: true
    },
    {
      id: 'scrum4sales',
      icon: Zap,
      gradient: 'from-orange-500 to-red-500',
      urgent: true
    },
    {
      id: 'change-sparring-6',
      icon: Shield,
      gradient: 'from-emerald-500 to-teal-600',
      urgent: false
    }
  ];
  const packages = Array.isArray(rawPackages)
    ? packagesMeta.map((meta) => {
        const item =
          rawPackages.find((pkg) => pkg.id === meta.id) ||
          rawPackages[0] ||
          {};
        return { ...meta, ...item };
      })
    : [];

  const rawResults = t('sosPage.results');
  const resultsMeta = [
    { id: 'productivity', icon: TrendingUp },
    { id: 'revenue', icon: Target },
    { id: 'delays', icon: Clock },
    { id: 'decisions', icon: CheckCircle }
  ];
  const results = Array.isArray(rawResults)
    ? resultsMeta.map((meta) => {
        const item =
          rawResults.find((r) => r.id === meta.id) || rawResults[0] || {};
        return { ...meta, ...item };
      })
    : [];

  const heroBullets = Array.isArray(t('sosPage.hero.bullets'))
    ? t('sosPage.hero.bullets')
    : [];
  const heroBulletIcons = [Clock, Shield, Target];

  const processSteps = Array.isArray(t('sosPage.process.steps'))
    ? t('sosPage.process.steps')
    : [];

  const contactBullets = Array.isArray(t('sosPage.contact.bullets'))
    ? t('sosPage.contact.bullets')
    : [];

  return (
    <div className="min-h-screen bg-white">
      {/* Hero Section */}
      <section className="relative py-32 bg-gradient-to-br from-red-900 via-red-800 to-orange-900 text-white overflow-hidden">
        <div className="absolute inset-0 overflow-hidden pointer-events-none">
          <div className="absolute top-1/4 -left-20 w-96 h-96 bg-gradient-to-r from-red-600/30 to-orange-500/30 rounded-full blur-3xl animate-float" />
          <div
            className="absolute bottom-1/4 -right-20 w-80 h-80 bg-gradient-to-r from-orange-500/30 to-red-600/30 rounded-full blur-3xl animate-float"
            style={{ animationDelay: '-3s' }}
          />
        </div>

        <div className="relative max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center">
            <div className="inline-flex items-center px-4 py-2 bg-red-600/20 rounded-full border border-red-400/30 mb-8">
              <Zap className="w-4 h-4 text-red-400 mr-2" />
              <span className="text-sm font-medium text-red-200">
                {t('sosPage.hero.badge')}
              </span>
            </div>

            <h1 className="text-4xl lg:text-6xl font-light text-white mb-8">
              <span className="gradient-text font-medium">
                {t('sosPage.hero.highlight')}
              </span>
              <br />
              {t('sosPage.hero.titleLine')}
            </h1>

            <p className="text-xl text-red-200 max-w-4xl mx-auto leading-relaxed font-light mb-12">
              {t('sosPage.hero.subtitle')}
            </p>

            <div className="flex items-center justify-center flex-wrap gap-6 text-red-200">
              {heroBullets.map((text, index) => {
                const Icon = heroBulletIcons[index] || Clock;
                return (
                  <div key={index} className="flex items-center">
                    <Icon className="w-5 h-5 mr-2" />
                    <span className="text-sm">{text}</span>
                  </div>
                );
              })}
            </div>
          </div>
        </div>
      </section>

      {/* SOS Packages */}
      <section className="py-32 bg-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-3xl lg:text-4xl font-light text-slate-900 mb-8">
              {t('sosPage.packagesSection.title')}{' '}
              <span className="gradient-text font-medium">
                {t('sosPage.packagesSection.highlight')}
              </span>
            </h2>
            <p className="text-xl text-slate-600 max-w-3xl mx-auto">
              {t('sosPage.packagesSection.subtitle')}
            </p>
          </div>

          <div className="grid lg:grid-cols-3 gap-8">
            {packages.map((pkg, index) => (
              <div
                key={pkg.id || index}
                className="group relative overflow-hidden rounded-3xl bg-white card-premium"
              >
                {/* Header with Badge */}
                <div
                  className={`relative h-48 bg-gradient-to-br ${pkg.gradient} flex flex-col items-center justify-center text-white p-6`}
                >
                  {pkg.urgent && (
                    <div className="absolute top-4 right-4 bg-red-500 text-white px-2 py-1 rounded-full text-xs font-bold animate-pulse">
                      {t('sosPage.labels.urgent')}
                    </div>
                  )}

                  <pkg.icon className="w-12 h-12 mb-4" />
                  <h3 className="text-xl font-medium text-center mb-2">
                    {pkg.title}
                  </h3>

                  <div className="inline-flex items-center px-3 py-1 bg-white/20 rounded-full text-xs font-medium">
                    <Clock className="w-3 h-3 mr-1" />
                    {pkg.badge}
                  </div>
                </div>

                {/* Content */}
                <div className="p-8">
                  <div className="mb-6">
                    <p className="text-sm font-medium text-purple-600 mb-2">
                      {pkg.duration}
                    </p>
                    <p className="text-slate-700 leading-relaxed">
                      {pkg.description}
                    </p>
                  </div>

                  <div className="space-y-3 mb-8">
                    {Array.isArray(pkg.features) &&
                      pkg.features.map((feature, idx) => (
                        <div key={idx} className="flex items-start">
                          <CheckCircle className="w-4 h-4 text-green-500 mr-2 mt-1 flex-shrink-0" />
                          <span className="text-sm text-slate-600">
                            {feature}
                          </span>
                        </div>
                      ))}
                  </div>

                  <p className="text-xs text-slate-500 mb-6">
                    {t('sosPage.packagesSection.footnote')}
                  </p>

                  <button className="w-full inline-flex items-center justify-center px-6 py-3 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-full font-medium transition-all duration-300 hover:shadow-lg hover:scale-105">
                    <Zap className="w-4 h-4 mr-2" />
                    <span className="mr-2">
                      {t('sosPage.actions.requestSOS')}
                    </span>
                    <ArrowRight className="w-4 h-4" />
                  </button>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Results Section */}
      <section className="py-32 bg-slate-50">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-3xl lg:text-4xl font-light text-slate-900 mb-8">
              {t('sosPage.resultsSection.titleBeforeHighlight')}{' '}
              <span className="gradient-text font-medium">
                {t('sosPage.resultsSection.highlight')}
              </span>
            </h2>
            <p className="text-xl text-slate-600 max-w-2xl mx-auto">
              {t('sosPage.resultsSection.subtitle')}
            </p>
          </div>

          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-8">
            {results.map((result, index) => (
              <div
                key={result.id || index}
                className="text-center p-8 bg-white rounded-3xl card-premium"
              >
                <result.icon className="w-12 h-12 text-purple-600 mx-auto mb-6" />
                <div className="text-4xl font-bold gradient-text mb-2">
                  {result.metric}
                </div>
                <div className="text-slate-600 font-medium">
                  {result.label}
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Emergency Process */}
      <section className="py-32 bg-slate-900 text-white">
        <div className="max-w-7xl mx-auto px-6 lg:px-8">
          <div className="text-center mb-20">
            <h2 className="text-3xl lg:text-4xl font-light text-white mb-8">
              {t('sosPage.process.titleBeforeHighlight')}{' '}
              <span className="gradient-text font-medium">
                {t('sosPage.process.highlight')}
              </span>
            </h2>
          </div>

          <div className="grid md:grid-cols-3 gap-8 text-center">
            {processSteps.map((step, index) => (
              <div key={index} className="p-8">
                <div
                  className={`w-16 h-16 mx-auto mb-6 rounded-full flex items-center justify-center text-white text-2xl font-bold ${
                    index === 0
                      ? 'bg-gradient-to-r from-red-600 to-orange-600'
                      : index === 1
                      ? 'bg-gradient-to-r from-orange-600 to-yellow-600'
                      : 'bg-gradient-to-r from-green-600 to-emerald-600'
                  }`}
                >
                  {index + 1}
                </div>
                <h3 className="text-xl font-medium text-white mb-4">
                  {step.title}
                </h3>
                <p className="text-slate-300">{step.text}</p>
              </div>
            ))}
          </div>

          <div className="text-center mt-16">
            <button className="inline-flex items-center px-12 py-6 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-full font-medium text-xl transition-all duration-300 hover:shadow-glow hover:scale-105">
              <Zap className="w-6 h-6 mr-3" />
              <span className="mr-3">{t('sosPage.actions.requestSOS')}</span>
              <ArrowRight className="w-6 h-6" />
            </button>
          </div>
        </div>
      </section>

      {/* Contact CTA */}
      <section className="py-32 bg-gradient-to-r from-purple-50 to-orange-50">
        <div className="max-w-4xl mx-auto px-6 lg:px-8 text-center">
          <h2 className="text-3xl lg:text-4xl font-light text-slate-900 mb-8">
            {t('sosPage.contact.titleBeforeHighlight')}{' '}
            <span className="gradient-text font-medium">
              {t('sosPage.contact.highlight')}
            </span>
            ?
          </h2>

          <p className="text-xl text-slate-600 leading-relaxed font-light mb-12">
            {t('sosPage.contact.text')}
          </p>

          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {contactBullets.map((item, index) => {
              const Icon =
                index === 0 ? Clock : index === 1 ? Shield : Target;
              const colorClass =
                index === 0
                  ? 'text-red-500'
                  : index === 1
                  ? 'text-green-500'
                  : 'text-purple-500';
              return (
                <div
                  key={index}
                  className="flex items-center justify-center text-slate-800"
                >
                  <Icon className={`w-6 h-6 ${colorClass} mr-2`} />
                  <span className="font-medium">{item}</span>
                </div>
              );
            })}
          </div>

          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <button className="inline-flex items-center px-8 py-4 bg-gradient-to-r from-red-600 to-orange-600 text-white rounded-full font-medium transition-all duration-300 hover:shadow-lg hover:scale-105">
              <span className="mr-2">
                {t('sosPage.contact.hotlineButton')}
              </span>
              <ArrowRight className="w-4 h-4" />
            </button>
            <button className="inline-flex items-center px-8 py-4 border-2 border-purple-600 text-purple-600 rounded-full font-medium transition-all duration-300 hover:bg-purple-600 hover:text-white">
              <span className="mr-2">
                {t('sosPage.contact.emailButton')}
              </span>
              <ArrowRight className="w-4 h-4" />
            </button>
          </div>
        </div>
      </section>
    </div>
  );
};
